import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";

import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

// Keep this identical to your contractor portal API_BASE.
const API_BASE = "https://portal.gmacc.co.za/api";

const EMPTY_FILTERS = {
  from: "",
  to: "",
  document: "",
  search: "",
};

const formatAmount = (amount) => {
  const value = Number(amount) || 0;
  const [integer, decimal] = value.toFixed(2).split(".");

  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, "\u2002") + "," + decimal;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  const parsedDate = new Date(`${dateValue}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return format(parsedDate, "dd MMM yyyy");
};

const CurrencyAmount = ({ value }) => (
  <span className={styles.bcCurrencyAmount}>
    <span>R</span>
    <span>{formatAmount(value)}</span>
  </span>
);

const LedgerPagination = ({ currentPage, total, perPage, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const start = total ? (currentPage - 1) * perPage + 1 : 0;
  const end = Math.min(currentPage * perPage, total);

  return (
    <div className={styles.pagination}>
      <button
        type="button"
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        disabled={currentPage <= 1}
        onClick={() => onChange(currentPage - 1)}
      >
        Previous
      </button>

      <span className={styles.pageMeta}>
        {start}–{end} of {total}
      </span>

      <button
        type="button"
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        disabled={currentPage >= totalPages}
        onClick={() => onChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
};

export default function ContractorAccountLedgerPage() {
  const navigate = useNavigate();
  const { contractorId, buildingId } = useParams();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  const [displayName, setDisplayName] = useState("My Profile ▾");
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);


  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;

      setIsDesktop(desktop);

      if (!desktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isDesktop || !sidebarOpen) return;

      const sidebar = document.getElementsByClassName(styles.sidebar)[0];

      const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

      if (!sidebar || !hamburger) return;

      const clickedInsideSidebar = sidebar.contains(event.target);
      const clickedHamburger = hamburger.contains(event.target);

      if (!clickedInsideSidebar && !clickedHamburger) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [sidebarOpen, isDesktop]);

  useEffect(() => {
    const loadUserAccess = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const encodedPayload = token.split(".")[1] || "";

        const normalisedPayload = encodedPayload
          .replace(/-/g, "+")
          .replace(/_/g, "/");

        const paddedPayload = normalisedPayload.padEnd(
          Math.ceil(normalisedPayload.length / 4) * 4,
          "=",
        );

        const payload = JSON.parse(atob(paddedPayload));

        const { email, contact_id, name, full_name, fullname } = payload || {};

        const profileName =
          full_name || fullname || name || email || "My Profile";

        setDisplayName(`${profileName} ▾`);

        const response = await fetch(`${API_BASE}/contact/determine-access/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email,
            contact_id,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setUserAccess(data.access || []);
        }
      } catch (accessError) {
        console.error("Failed to load user access:", accessError);
      } finally {
        setLoadingAccess(false);
      }
    };

    loadUserAccess();
  }, [navigate]);

  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 25;

  const buildQueryString = useCallback((filters) => {
    const query = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      const cleanedValue = String(value || "").trim();

      if (cleanedValue) {
        query.set(key, cleanedValue);
      }
    });

    const queryString = query.toString();
    return queryString ? `?${queryString}` : "";
  }, []);

  const loadLedger = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        const queryString = buildQueryString(appliedFilters);

        const response = await fetch(
          `${API_BASE}/contractor-id/${contractorId}/bc-accounts/${buildingId}/ledger/${queryString}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Failed to load the vendor ledger.");
        }

        setLedgerData(data);
        setCurrentPage(1);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [appliedFilters, buildQueryString, buildingId, contractorId],
  );

  useEffect(() => {
    const controller = new AbortController();

    loadLedger(controller.signal);

    return () => controller.abort();
  }, [loadLedger]);

  const entries = useMemo(() => {
    const rows = Array.isArray(ledgerData?.value) ? ledgerData.value : [];

    return [...rows].sort((a, b) => {
      const dateComparison = String(b.date || "").localeCompare(
        String(a.date || ""),
      );

      if (dateComparison !== 0) {
        return dateComparison;
      }

      // Newest BC entry first when multiple entries share the same date.
      return String(b.entry_number || "").localeCompare(
        String(a.entry_number || ""),
        undefined,
        { numeric: true },
      );
    });
  }, [ledgerData]);

  const currentRows = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return entries.slice(start, start + perPage);
  }, [entries, currentPage]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setDraftFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    setDraftFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const queryString = buildQueryString(appliedFilters);

      const response = await fetch(
        `${API_BASE}/contractor-id/${contractorId}/bc-accounts/${buildingId}/ledger/export/${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(data.error || "Failed to export the vendor ledger.");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const contentDisposition =
        response.headers.get("Content-Disposition") || "";

      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

      const filename = filenameMatch?.[1]
        ? filenameMatch[1]
        : "vendor-ledger.xlsx";

      const anchor = document.createElement("a");

      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  return (
  <div className={styles.page}>
    <header className={styles.topbar}>
      <div className={styles.topbarLeft}>
        <img
          src={logoimage}
          alt="GMA Logo"
          className={styles.topLogo}
        />

        <button
          type="button"
          className={styles.menuBtn}
          aria-label="Open navigation"
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen((current) => !current)}
        >
          ☰
        </button>

        <h1 className={styles.topTitle}>
          {ledgerData?.building?.name || "MY ACCOUNT"}
        </h1>
      </div>

      <div
        className={styles.userChip}
        onClick={() => navigate("/user-profile")}
        style={{ cursor: "pointer" }}
      >
        <span className={styles.statusDot}></span>
        <span className={styles.userName}>{displayName}</span>
      </div>
    </header>

    <div className={styles.shell}>
<PortalSidebar
  activePortal="contractor"
  activeTab="accounts"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole={null}
/>

      <main className={styles.contentArea}>
        <div className={styles.panel}>
          <section className={styles.bcLedgerCard}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingImage}></div>
              <p className={styles.loadingText}>Loading vendor ledger...</p>
            </div>
          ) : error && !ledgerData ? (
            <div>
              <p className={styles.errorText}>{error}</p>

              <button
                type="button"
                className={styles.bcPrimaryButton}
                onClick={() => navigate(-1)}
              >
                Return to My Accounts
              </button>
            </div>
          ) : (
            <>
              <div className={styles.bcLedgerHeading}>
                <div>
                  <h2>{ledgerData?.building?.name || "Vendor Account"}</h2>

                  <p>
                    Vendor Code: <strong>{ledgerData?.vendor?.code}</strong>
                  </p>

                  {ledgerData?.vendor?.name && <p>{ledgerData.vendor.name}</p>}
                </div>

                <div className={styles.bcBalanceCard}>
                  <span>Account Balance</span>

                  <strong>
                    <CurrencyAmount value={ledgerData?.vendor?.balance} />
                  </strong>
                </div>
              </div>

              <form
                className={styles.bcLedgerFilters}
                onSubmit={handleApplyFilters}
              >
                <label>
                  <span>From date</span>
                  <input
                    type="date"
                    name="from"
                    value={draftFilters.from}
                    onChange={handleFilterChange}
                  />
                </label>

                <label>
                  <span>To date</span>
                  <input
                    type="date"
                    name="to"
                    value={draftFilters.to}
                    onChange={handleFilterChange}
                  />
                </label>

                <label>
                  <span>Document number</span>
                  <input
                    type="text"
                    name="document"
                    placeholder="Search document number…"
                    value={draftFilters.document}
                    onChange={handleFilterChange}
                  />
                </label>

                <label>
                  <span>Description / external reference</span>
                  <input
                    type="text"
                    name="search"
                    placeholder="Search description or reference…"
                    value={draftFilters.search}
                    onChange={handleFilterChange}
                  />
                </label>

                <div className={styles.bcLedgerFilterActions}>
                  <button type="submit" className={styles.bcPrimaryButton}>
                    Apply Filters
                  </button>

                  <button
                    type="button"
                    className={styles.bcSecondaryButton}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    className={styles.bcExportButton}
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting ? "Exporting..." : "Export to Excel"}
                  </button>
                </div>
              </form>

              {error && <p className={styles.errorText}>{error}</p>}

              {!entries.length ? (
                <p className={styles.infoText}>
                  No ledger entries match the selected filters.
                </p>
              ) : (
                <>
                  <div className={styles.responsiveTableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Doc Number</th>
                          <th>Description</th>
                          <th>External Ref Number</th>
                          <th>Amount</th>
                          <th>Running Balance</th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentRows.map((entry) => (
                          <tr
                            key={
                              entry.entry_number ||
                              `${entry.date}-${entry.document_number}`
                            }
                          >
                            <td>{formatDate(entry.date)}</td>
                            <td>{entry.document_number}</td>
                            <td>{entry.description}</td>
                            <td>{entry.external_reference_number}</td>
                            <td>
                              <CurrencyAmount value={entry.amount} />
                            </td>
                            <td>
                              <CurrencyAmount value={entry.running_balance} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <LedgerPagination
                    currentPage={currentPage}
                    total={entries.length}
                    perPage={perPage}
                    onChange={setCurrentPage}
                  />
                </>
              )}
            </>
          )}
          </section>
        </div>
      </main>
    </div>
  </div>
);
}
